import type { ParentAnalyticsResult } from "@/hooks/useParentAnalytics";
import type { PipelineRow, ScorecardRow, CommandStatus } from "./commandMetrics";

/**
 * Single source-of-truth "current parent priority" for the Parent Hub.
 *
 * Every intelligence card in the hub (Primary Signal → Evidence → Readiness
 * emphasis → Recommended Action → Conversation Starter → Mentor Pulse)
 * MUST consume the same priority so the hub feels like one connected
 * intelligence system rather than unrelated cards.
 *
 * Derived purely from existing front-end data — no backend changes.
 */

export type PriorityConcept =
  | "needs_vs_wants"
  | "delayed_gratification"
  | "cash_flow"
  | "opportunity_cost"
  | "financial_tradeoffs"
  | "budget_builder"
  | "credit_score"
  | "borrowing_money"
  | "minimum_payments"
  | "credit_management"
  | "urgency_red_flag"
  | "too_good_returns"
  | "scam_defense"
  | "general";

export type PriorityModule = "budget" | "credit" | "scam" | "general";

export interface ParentPriority {
  concept: PriorityConcept;
  /** Full display label, e.g. "Borrowing Money". */
  label: string;
  /** Short, scan-friendly label for tiles, e.g. "Borrowing". */
  shortLabel: string;
  module: PriorityModule;
  /** Short module label for tiles, e.g. "Credit & Debt". */
  moduleLabel: string;
  /** 1-indexed position in the 13-step beta path, if applicable. */
  betaIndex: number | null;
  /** Why this concept is the current priority. */
  evidence: string;
  /** Forward-looking signal to watch. */
  watch: string;
  /** Recommended parent action — concept-aware, never generic. */
  action: { title: string; reason: string };
  /** Conversation starter — concept-aware, with parent-facing rationale. */
  prompt: { text: string; why: string };
  /** Mentor angle for Mentor Pulse — single most relevant mentor read. */
  mentor: { name: string; insight: string };
  /** Status chip tone used by the primary signal panel. */
  tone: "forming" | "watch" | "ready";
  statusChip: string;
}

const BETA_PATH_INDEX: Record<PriorityConcept, number | null> = {
  needs_vs_wants: 1,
  delayed_gratification: 2,
  cash_flow: 3,
  opportunity_cost: 4,
  financial_tradeoffs: 5,
  budget_builder: 6,
  credit_score: 7,
  borrowing_money: 8,
  minimum_payments: 9,
  credit_management: 10,
  urgency_red_flag: 11,
  too_good_returns: 12,
  scam_defense: 13,
  general: null,
};

const MODULE_OF: Record<PriorityConcept, PriorityModule> = {
  needs_vs_wants: "budget",
  delayed_gratification: "budget",
  cash_flow: "budget",
  opportunity_cost: "budget",
  financial_tradeoffs: "budget",
  budget_builder: "budget",
  credit_score: "credit",
  borrowing_money: "credit",
  minimum_payments: "credit",
  credit_management: "credit",
  urgency_red_flag: "scam",
  too_good_returns: "scam",
  scam_defense: "scam",
  general: "general",
};

const MODULE_LABELS: Record<PriorityModule, string> = {
  budget: "Money Foundations",
  credit: "Credit & Debt",
  scam: "Scam Defense",
  general: "Learning Path",
};

const REGISTRY: Record<PriorityConcept, { label: string; short: string; mentorName: string; mentorInsight: string; prompt: string; promptWhy: string; actionTitle: string; actionReason: string; watch: string }> = {
  needs_vs_wants: {
    label: "Needs vs Wants", short: "Needs vs Wants",
    mentorName: "Aelyra Solari", mentorInsight: "This is the foundational filter. Help them name one need and one want from this week before the next lesson.",
    prompt: "How would you decide whether something this week is a need or a want?",
    promptWhy: "This checks whether they can apply the filter to their own life, not just a textbook example.",
    actionTitle: "Walk through Needs vs Wants together",
    actionReason: "First concept in the learning path. Naming a real example makes it stick.",
    watch: "Whether they can defend the difference using a real purchase from this week.",
  },
  delayed_gratification: {
    label: "Delayed Gratification", short: "Delay",
    mentorName: "Aelyra Solari", mentorInsight: "Patience compounds. Ask what they would buy if they waited 30 days.",
    prompt: "What's one thing you wanted last month that you're glad you didn't buy?",
    promptWhy: "This reveals whether they can recognize impulse purchases in hindsight.",
    actionTitle: "Review Delayed Gratification together",
    actionReason: "Builds the patience muscle every later money skill depends on.",
    watch: "Whether they can pause on one impulse decision this week.",
  },
  cash_flow: {
    label: "Cash Flow", short: "Cash Flow",
    mentorName: "Aelyra Solari", mentorInsight: "Cash flow is the heartbeat of money. Ask them to map one week of money in vs money out.",
    prompt: "If money came in on Friday and bills hit on Monday, how would you plan that week?",
    promptWhy: "This checks timing awareness — most early money mistakes are timing mistakes.",
    actionTitle: "Walk through Cash Flow together",
    actionReason: "Cash flow is where budgeting becomes real. Practice it with a real week.",
    watch: "Whether they connect when money arrives with when bills are due.",
  },
  opportunity_cost: {
    label: "Opportunity Cost", short: "Opportunity",
    mentorName: "Caelion Veyr", mentorInsight: "Every yes is a no to something else. Ask what they gave up the last time they said yes.",
    prompt: "When you said yes to your last purchase, what did you say no to?",
    promptWhy: "This trains tradeoff awareness — the core of all financial decisions.",
    actionTitle: "Review Opportunity Cost together",
    actionReason: "Tradeoff thinking is the foundation under every later decision.",
    watch: "Whether they can name the tradeoff before the next purchase, not after.",
  },
  financial_tradeoffs: {
    label: "Financial Tradeoffs", short: "Tradeoffs",
    mentorName: "Caelion Veyr", mentorInsight: "They're practicing reading tradeoffs. Ask what they give up when they pick the easier option now.",
    prompt: "What's a recent choice where the easy option cost more in the long run?",
    promptWhy: "This checks whether they can spot long-term cost hiding inside short-term ease.",
    actionTitle: "Discuss Financial Tradeoffs together",
    actionReason: "Tradeoff reasoning is what separates impulse from intent.",
    watch: "Whether they can articulate the long-term cost of a short-term choice.",
  },
  budget_builder: {
    label: "Budget Builder", short: "Budget",
    mentorName: "Aelyra Solari", mentorInsight: "A budget is a plan, not a rule. Ask them to sketch a one-week budget with you.",
    prompt: "If you had $200 for the week, how would you split spending, saving, and giving?",
    promptWhy: "This reveals their default priorities — and where to gently coach.",
    actionTitle: "Open Budget Builder together",
    actionReason: "Hands-on practice cements the prerequisite lessons.",
    watch: "Whether their plan accounts for both fixed and flexible spending.",
  },
  credit_score: {
    label: "Credit Score Basics", short: "Credit",
    mentorName: "Aelyra Solari", mentorInsight: "A credit score is a trust signal. Help them connect on-time payments to future opportunities.",
    prompt: "Why might a future landlord or lender care about a credit score?",
    promptWhy: "This checks whether they see credit as future-leverage, not just a number.",
    actionTitle: "Continue the next Credit & Debt lesson",
    actionReason: "Credit foundations open into Borrowing Money next.",
    watch: "Whether they connect daily habits to a long-term trust signal.",
  },
  borrowing_money: {
    label: "Borrowing Money", short: "Borrowing",
    mentorName: "Aelyra Solari", mentorInsight: "Borrowing confidence is forming. Compare the monthly payment with the total cost together.",
    prompt: "How would you decide whether a monthly payment is actually affordable?",
    promptWhy: "This checks whether they understand affordability beyond the monthly number.",
    actionTitle: "Review Borrowing Money together",
    actionReason: "This concept sits right before Minimum Payments Trap — best reviewed together.",
    watch: "Whether they look past the monthly payment to total cost.",
  },
  minimum_payments: {
    label: "Minimum Payments Trap", short: "Min Payments",
    mentorName: "Aelyra Solari", mentorInsight: "Minimum payments hide real cost. Walk through a small balance and how long it takes to clear.",
    prompt: "What happens if someone only pays the minimum every month?",
    promptWhy: "This reveals whether they understand interest and long-term cost.",
    actionTitle: "Discuss minimum payments before the next credit lesson",
    actionReason: "Catching this trap before the Credit Management module changes the whole frame.",
    watch: "Whether they spot interest as the silent driver of debt cost.",
  },
  credit_management: {
    label: "Credit Management", short: "Credit Mgmt",
    mentorName: "Aelyra Solari", mentorInsight: "Managing credit is a habit, not a moment. Ask what their monthly credit routine would look like.",
    prompt: "What would a healthy monthly credit routine look like for you?",
    promptWhy: "This pushes from knowing to doing — habits are where credit health lives.",
    actionTitle: "Open Credit Management together",
    actionReason: "Bridges credit knowledge into a real, repeatable habit.",
    watch: "Whether they design a routine, not just recall a rule.",
  },
  urgency_red_flag: {
    label: "Urgency as a Red Flag", short: "Urgency",
    mentorName: "Caelion Veyr", mentorInsight: "Urgency hijacks decisions. Ask what would make an offer feel urgent — and why that's the warning.",
    prompt: "What would make an offer feel urgent, and why could that be a warning sign?",
    promptWhy: "This checks whether they can recognize pressure tactics before acting.",
    actionTitle: "Review Urgency as a Red Flag together",
    actionReason: "Recognizing pressure is the first defense before Scam Defense.",
    watch: "Whether they can name an urgency cue from real life this week.",
  },
  too_good_returns: {
    label: "Too-Good-To-Be-True Returns", short: "Too-Good Returns",
    mentorName: "Caelion Veyr", mentorInsight: "Unrealistic returns are a tell. Ask what they would check before trusting easy money.",
    prompt: "What would you check before trusting an investment that promises easy money?",
    promptWhy: "This reveals whether they can separate opportunity from unrealistic claims.",
    actionTitle: "Discuss Too-Good-To-Be-True Returns together",
    actionReason: "Locks in skepticism before Scam Defense scenarios begin.",
    watch: "Whether they ask for proof before trusting a return.",
  },
  scam_defense: {
    label: "Scam Defense", short: "Scam Defense",
    mentorName: "Caelion Veyr", mentorInsight: "Scam defense is pattern recognition under pressure. Test one scenario with them this week.",
    prompt: "If someone you don't know asked for your bank details, what would you do first?",
    promptWhy: "This checks whether the default response is verify-first, not respond-first.",
    actionTitle: "Start the Scam Defense module together",
    actionReason: "Capstone of the learning path — best practiced side by side.",
    watch: "Whether their first instinct is to verify, not to act.",
  },
  general: {
    label: "Learning Path", short: "Learning Path",
    mentorName: "Lumi Vanta", mentorInsight: "A good next question is: what changes if this decision repeats every month?",
    prompt: "What's one thing you learned recently that surprised you?",
    promptWhy: "Opens the door without putting them on the spot.",
    actionTitle: "Pick the next lesson together",
    actionReason: "Short, shared sessions build the cadence the learning path is designed around.",
    watch: "Whether one short session this week restarts the cadence.",
  },
};

function matchConceptFromText(text: string | null | undefined): PriorityConcept | null {
  if (!text) return null;
  const t = text.toLowerCase();
  const tests: [string[], PriorityConcept][] = [
    [["needs vs wants", "needs and wants", "need vs want"], "needs_vs_wants"],
    [["delayed gratification", "patience"], "delayed_gratification"],
    [["cash flow"], "cash_flow"],
    [["opportunity cost"], "opportunity_cost"],
    [["tradeoff", "trade-off", "tradeoffs"], "financial_tradeoffs"],
    [["budget builder", "budget"], "budget_builder"],
    [["credit score"], "credit_score"],
    [["borrow", "loan"], "borrowing_money"],
    [["minimum payment", "min payment"], "minimum_payments"],
    [["credit management", "manage credit"], "credit_management"],
    [["urgency", "red flag"], "urgency_red_flag"],
    [["too good", "unrealistic return", "guaranteed return"], "too_good_returns"],
    [["scam", "fraud", "phishing"], "scam_defense"],
  ];
  for (const [keys, concept] of tests) {
    if (keys.some((k) => t.includes(k))) return concept;
  }
  return null;
}

function fallbackConceptForModule(moduleId: string): PriorityConcept {
  if (moduleId === "credit") return "borrowing_money";
  if (moduleId === "scam") return "scam_defense";
  if (moduleId === "budget") return "cash_flow";
  return "general";
}

export function deriveCurrentPriority(args: {
  analytics: ParentAnalyticsResult;
  pipeline: PipelineRow[];
  scorecard: ScorecardRow[];
  status: CommandStatus;
  pathLabel: string;
}): ParentPriority {
  const { analytics, pipeline, status, pathLabel } = args;

  let concept: PriorityConcept | null = null;

  // 1. Empty / re-engage → general fallback (or recent path concept if any).
  if (status.state === "empty") {
    concept = "needs_vs_wants";
  } else {
    // 2. Lowest-readiness active beta module → most-recent concept in that area.
    const active = pipeline.filter((p) => !p.advanced && p.readiness > 0);
    const lowestActive = active.sort((a, b) => a.readiness - b.readiness)[0];
    if (lowestActive) {
      // Look through recent activity for a concept inside this module first.
      for (const ev of analytics.recentActivity) {
        const fromTitle = matchConceptFromText(ev.title);
        if (fromTitle && MODULE_OF[fromTitle] === lowestActive.id) {
          concept = fromTitle;
          break;
        }
      }
      if (!concept) concept = fallbackConceptForModule(lowestActive.id);
    }

    // 3. Fall back to most-recent activity title across any concept.
    if (!concept) {
      for (const ev of analytics.recentActivity) {
        const fromTitle = matchConceptFromText(ev.title);
        if (fromTitle) { concept = fromTitle; break; }
      }
    }

    // 4. Fall back to currentPath label.
    if (!concept) concept = matchConceptFromText(pathLabel) ?? "general";
  }

  const reg = REGISTRY[concept];
  const moduleId = MODULE_OF[concept];
  const tone: ParentPriority["tone"] = status.state === "reengage" ? "watch" : status.state === "empty" ? "forming" : "forming";
  const statusChip = status.state === "reengage" ? "Watch" : status.state === "empty" ? "Forming" : "Forming";

  // Phase 2 — beta-scoped count. Drives KpiStrip's "Learning Path" tile, the
  // pipeline evidence, and this string so all three always agree.
  const lessons = analytics.betaPathCompleted;
  const isFinalBeta = BETA_PATH_INDEX[concept] === 13;
  const evidence = status.state === "empty"
    ? "No lessons recorded yet. This is the natural first concept in the learning path."
    : status.state === "reengage"
      ? `Activity has cooled. ${lessons} of 13 learning moments complete — a short shared session here restarts the cadence.`
      : isFinalBeta
        ? `${lessons} of 13 learning moments complete. ${reg.label} is the final module.`
        : `${lessons} of 13 learning moments complete. ${reg.label} is the current edge of ${MODULE_LABELS[moduleId]}.`;

  return {
    concept,
    label: reg.label,
    shortLabel: reg.short,
    module: moduleId,
    moduleLabel: MODULE_LABELS[moduleId],
    betaIndex: BETA_PATH_INDEX[concept],
    evidence,
    watch: reg.watch,
    action: { title: reg.actionTitle, reason: reg.actionReason },
    prompt: { text: reg.prompt, why: reg.promptWhy },
    mentor: { name: reg.mentorName, insight: reg.mentorInsight },
    tone,
    statusChip,
  };
}

/**
 * Beta-safe evidence string for a pipeline row — replaces full-curriculum
 * "X of 33 prerequisite lessons" language with beta path language.
 */
export function betaSafePipelineEvidence(row: PipelineRow, totalBetaLessons: number): string {
  if (row.advanced) return row.evidence;
  const completed = Math.min(totalBetaLessons, 13);
  return `${completed} of 13 learning moments complete${row.readiness > 0 ? ` — ${row.name} is currently building.` : ` — ${row.name} hasn't started yet.`}`;
}